import React, { useState, useEffect, useRef } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { EXTENDED_DATA_SCHEMA_TYPES, LISTING_STATE_DRAFT, propTypes } from '../../../../util/types';
// Import configs and util modules
import { LISTING_PAGE_PARAM_TYPE_NEW } from '../../../../util/urlHelpers'; import {
  isFieldForCategory,
  isFieldForListingType,
  isValidCurrencyForTransactionProcess,
} from '../../../../util/fieldHelpers';
import { maxLength, required, composeValidators } from '../../../../util/validators';

// Import shared components
import {
  Form,
  Button,
  FieldSelect,
  FieldTextInput,
  Heading,
  CustomExtendedDataField,
  OutsideClickHandler,
} from '../../../../components';
// Import modules from this directory
import css from './EditListingDetailsForm.module.css';
import { countries, languages } from '../../../../config/configListing';
import ExpandableDropdown from '../../../ExpandableDropdown/ExpandableDropdown';
import axios from 'axios';

const mapboxToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const PREDICTIONS_KEY = 'predictions';
const MAX_PREDICTIONS = 3;
const TITLE_MAX_LENGTH = 80;
const SLOGAN_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 700;
const getSupportedCountryCodes = supportedCountries => supportedCountries.map(c => c.code);

// Show various error messages
const ErrorMessage = props => {
  const { fetchErrors } = props;
  const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
  const errorMessage = updateListingError ? (
    <FormattedMessage id="EditListingDetailsForm.updateFailed" />
  ) : createListingDraftError ? (
    <FormattedMessage id="EditListingDetailsForm.createListingDraftError" />
  ) : showListingsError ? (
    <FormattedMessage id="EditListingDetailsForm.showListingFailed" />
  ) : null;

  if (errorMessage) {
    return <p className={css.error}>{errorMessage}</p>;
  }
  return null;
};

const getCountryFromPrediction = (prediction) => {
  const { place_name, place_type, context } = prediction;
  return place_type.includes('country') ? place_name : context.find(itm => itm.id.includes('country'))?.text;
}

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

// Field component that either allows selecting listing type (if multiple types are available)
// or just renders hidden fields:
// - listingType              Set of predefined configurations for each listing type
// - transactionProcessAlias  Initiate correct transaction against Marketplace API
// - unitType                 Main use case: pricing unit
const FieldSelectListingType = props => {
  const {
    name,
    listingTypes,
    hasExistingListingType,
    onListingTypeChange,
    formApi,
    formId,
    intl,
  } = props;
  const hasMultipleListingTypes = listingTypes?.length > 1;

  const handleOnChange = value => {
    const selectedListingType = listingTypes.find(config => config.listingType === value);
    formApi.change('transactionProcessAlias', selectedListingType.transactionProcessAlias);
    formApi.change('unitType', selectedListingType.unitType);

    if (onListingTypeChange) {
      onListingTypeChange(selectedListingType);
    }
  };
  const getListingTypeLabel = listingType => {
    const listingTypeConfig = listingTypes.find(config => config.listingType === listingType);
    return listingTypeConfig ? listingTypeConfig.label : listingType;
  };

  return hasMultipleListingTypes && !hasExistingListingType ? (
    <>
      <FieldSelect
        id={formId ? `${formId}.${name}` : name}
        name={name}
        className={css.listingTypeSelect}
        label={intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeLabel' })}
        validate={required(
          intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeRequired' })
        )}
        onChange={handleOnChange}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'EditListingDetailsForm.listingTypePlaceholder' })}
        </option>
        {listingTypes.map(config => {
          const type = config.listingType;
          return (
            <option key={type} value={type}>
              {config.label}
            </option>
          );
        })}
      </FieldSelect>
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </>
  ) : hasMultipleListingTypes && hasExistingListingType ? (
    <div className={css.listingTypeSelect}>
      <Heading as="h5" rootClassName={css.selectedLabel}>
        {intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeLabel' })}
      </Heading>
      <p className={css.selectedValue}>{getListingTypeLabel(formApi.getFieldState(name)?.value)}</p>
      <FieldHidden name={name} />
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </div>
  ) : (
    <>
      <FieldHidden name={name} />
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </>
  );
};

// Finds the correct subcategory within the given categories array based on the provided categoryIdToFind.
const findCategoryConfig = (categories, categoryIdToFind) => {
  return categories?.find(category => category.id === categoryIdToFind);
};

/**
 * Recursively render subcategory field inputs if there are subcategories available.
 * This function calls itself with updated props to render nested category fields.
 * The select field is used for choosing a category or subcategory.
 */
const CategoryField = props => {
  const { currentCategoryOptions, level, values, prefix, handleCategoryChange, intl } = props;

  const currentCategoryKey = `${prefix}${level}`;

  const categoryConfig = findCategoryConfig(currentCategoryOptions, values[`${prefix}${level}`]);

  return (
    <>
      {currentCategoryOptions ? (
        <FieldSelect
          key={currentCategoryKey}
          id={currentCategoryKey}
          name={currentCategoryKey}
          className={css.listingTypeSelect}
          onChange={event => handleCategoryChange(event, level, currentCategoryOptions)}
          label={intl.formatMessage(
            { id: 'EditListingDetailsForm.categoryLabel' },
            { categoryLevel: currentCategoryKey }
          )}
          validate={required(
            intl.formatMessage(
              { id: 'EditListingDetailsForm.categoryRequired' },
              { categoryLevel: currentCategoryKey }
            )
          )}
        >
          <option disabled value="">
            {intl.formatMessage(
              { id: 'EditListingDetailsForm.categoryPlaceholder' },
              { categoryLevel: currentCategoryKey }
            )}
          </option>

          {currentCategoryOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </FieldSelect>
      ) : null}

      {categoryConfig?.subcategories?.length > 0 ? (
        <CategoryField
          currentCategoryOptions={categoryConfig.subcategories}
          level={level + 1}
          values={values}
          prefix={prefix}
          handleCategoryChange={handleCategoryChange}
          intl={intl}
        />
      ) : null}
    </>
  );
};

const FieldSelectCategory = props => {
  useEffect(() => {
    checkIfInitialValuesExist();
  }, []);

  const { prefix, listingCategories, formApi, intl, setAllCategoriesChosen, values } = props;

  // Counts the number of selected categories in the form values based on the given prefix.
  const countSelectedCategories = () => {
    return Object.keys(values).filter(key => key.startsWith(prefix)).length;
  };

  // Checks if initial values exist for categories and sets the state accordingly.
  // If initial values exist, it sets `allCategoriesChosen` state to true; otherwise, it sets it to false
  const checkIfInitialValuesExist = () => {
    const count = countSelectedCategories(values, prefix);
    setAllCategoriesChosen(count > 0);
  };

  // If a parent category changes, clear all child category values
  const handleCategoryChange = (category, level, currentCategoryOptions) => {
    const selectedCatLenght = countSelectedCategories();
    if (level < selectedCatLenght) {
      for (let i = selectedCatLenght; i > level; i--) {
        formApi.change(`${prefix}${i}`, null);
      }
    }
    const categoryConfig = findCategoryConfig(currentCategoryOptions, category).subcategories;
    setAllCategoriesChosen(!categoryConfig || categoryConfig.length === 0);
  };

  return (
    <CategoryField
      currentCategoryOptions={listingCategories}
      level={1}
      values={values}
      prefix={prefix}
      handleCategoryChange={handleCategoryChange}
      intl={intl}
    />
  );
};

// Add collect data for listing fields (both publicData and privateData) based on configuration
const AddListingFields = props => {
  const { listingType, listingFieldsConfig, selectedCategories, formId, intl } = props;
  const targetCategoryIds = Object.values(selectedCategories);

  const fields = listingFieldsConfig.reduce((pickedFields, fieldConfig) => {
    const { key, schemaType, scope } = fieldConfig || {};
    const namespacedKey = scope === 'public' ? `pub_${key}` : `priv_${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isProviderScope = ['public', 'private'].includes(scope);
    const isTargetListingType = isFieldForListingType(listingType, fieldConfig);
    const isTargetCategory = isFieldForCategory(targetCategoryIds, fieldConfig);

    return isKnownSchemaType && isProviderScope && isTargetListingType && isTargetCategory
      ? [
        ...pickedFields,
        <CustomExtendedDataField
          key={namespacedKey}
          name={namespacedKey}
          fieldConfig={fieldConfig}
          defaultRequiredMessage={intl.formatMessage({
            id: 'EditListingDetailsForm.defaultRequiredMessage',
          })}
          formId={formId}
        />,
      ]
      : pickedFields;
  }, []);

  return <>{fields}</>;
};

/**
 * Get predictions from the local storage
 * @returns predictions array
 */
const getPredictions = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const storedPredictions = window.localStorage.getItem(PREDICTIONS_KEY);
  return storedPredictions ? JSON.parse(storedPredictions) : [];
};

// Form that asks title, description, transaction process and unit type for pricing
// In addition, it asks about custom fields according to marketplace-custom-config.js
const EditListingDetailsFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        autoFocus,
        className,
        disabled,
        ready,
        formId,
        form: formApi,
        handleSubmit,
        onListingTypeChange,
        intl,
        invalid,
        pristine,
        marketplaceCurrency,
        marketplaceName,
        selectableListingTypes,
        selectableCategories,
        hasExistingListingType,
        pickSelectedCategories,
        categoryPrefix,
        saveActionMsg,
        draftButtonText,
        updated,
        updateInProgress,
        saveAsDraftInProgress,
        fetchErrors,
        listingFieldsConfig,
        listingCurrency,
        values,
        params,
      } = formRenderProps;

      const { listingType, transactionProcessAlias, unitType, state, country } = values;
      const [allCategoriesChosen, setAllCategoriesChosen] = useState(false);
      const [predictions, setPredictions] = useState([]);
      const [countryInput, setCountryInput] = useState(country);
      const [countryError, setCountryError] = useState('');
      const [countryInputFocused, setCountryInputFocused] = useState(false);
      const countryDropdownRef = useRef(null);

      const storedPredictions = getPredictions();
      const renderablePredictions =
        predictions && predictions.length > 0
          ? predictions
          : storedPredictions && storedPredictions.length > 0
            ? storedPredictions
            : [];

      // Show predictions only if input is in focused.
      const showPredictions = countryInputFocused;

      useEffect(() => {
        if (countryInput) {
          fetchPredictions(countryInput);
        } else {
          setPredictions([]);
        }
      }, [countryInput]);

      const titleRequiredMessage = intl.formatMessage({
        id: 'EditListingDetailsForm.titleRequired',
      });
      const maxLengthMessage = intl.formatMessage(
        { id: 'EditListingDetailsForm.maxLength' },
        {
          maxLength: TITLE_MAX_LENGTH,
        }
      );

      // Determine the currency to validate:
      // - If editing an existing listing, use the listing's currency.
      // - If creating a new listing, fall back to the default marketplace currency.
      const currencyToCheck = listingCurrency || marketplaceCurrency;

      // Verify if the selected listing type's transaction process supports the chosen currency.
      // This checks compatibility between the transaction process
      // and the marketplace or listing currency.
      const isCompatibleCurrency = isValidCurrencyForTransactionProcess(
        transactionProcessAlias,
        currencyToCheck
      );

      const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);

      const hasCategories = selectableCategories && selectableCategories.length > 0;
      const showCategories = listingType && hasCategories;

      const showTitle = hasCategories ? allCategoriesChosen : listingType;
      const showDescription = hasCategories ? allCategoriesChosen : listingType;
      const showListingFields = hasCategories ? allCategoriesChosen : listingType;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const hasMandatoryListingTypeData = listingType && transactionProcessAlias && unitType;
      const isCountrySelected = formApi.getState().values?.country ? true : false;
      console.log('hasMandatoryListingTypeData', !hasMandatoryListingTypeData ,!isCountrySelected,
        !isCompatibleCurrency)
      const submitDisabled =
        invalid ||
        disabled ||
        submitInProgress ||
        !hasMandatoryListingTypeData || !isCountrySelected ||
        !isCompatibleCurrency;

      const fetchPredictions = async query => {
        try {
          const response = await axios.get(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${mapboxToken}`
          );
          setPredictions(response.data.features);
        } catch (error) {
          console.error('Error fetching predictions:', error);
        }
      };

      const handleInputChange = e => {
        formApi.change('country', null);
        setCountryInput(e.target.value);
        setCountryError('');
      };

      const handleInputBlur = async (e) => {
        setCountryInputFocused(false);
      };

      const handleMouseEnter = index => {
        const items = countryDropdownRef.current.querySelectorAll('li');
        items.forEach((item, idx) => {
          item.classList.toggle(css.highlighted, idx === index);
        });
      };

      const handlePredictionClick = prediction => {
        if (!prediction) return null;
        const countryContext = prediction.context?.find((item) =>
          item.id.startsWith("country")
        );
        const countryName = countryContext ? countryContext.text : prediction.place_name;

        setCountryInput(countryName);
        setCountryError('');
        setCountryInputFocused(false);
        formApi.change('country', getCountryFromPrediction(prediction));
      };

      const { type } = params || {};
      const isNewURI = type === LISTING_PAGE_PARAM_TYPE_NEW;
      const showDraftButton = isNewURI || state === LISTING_STATE_DRAFT;
      const titleLength = values?.title?.length || 0;
      const discriptionLength = values?.description?.length || 0;
      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <ErrorMessage fetchErrors={fetchErrors} />

          <div className={css.inputBox}>
            <FieldTextInput
              id={`${formId}title`}
              name="title"
              // className={css.title}
              type="text"
              maxLength={TITLE_MAX_LENGTH}
              label={intl.formatMessage({ id: 'EditListingDetailsForm.slogan' })}
              placeholder={intl.formatMessage({
                id: 'EditListingDetailsForm.sloganPlaceholder',
              })}
              validate={composeValidators(
                required(
                  intl.formatMessage({
                    id: 'EditListingDetailsForm.descriptionRequired',
                  })
                )
              )}
            />
            {/* charactor count */}
            <p className={css.destinationCharCount}>{titleLength}/{SLOGAN_MAX_LENGTH}</p>
          </div>


          <OutsideClickHandler className={css.country} onOutsideClick={handleInputBlur}>
            <label>
              {intl.formatMessage({
                id: 'EditListingDetailsForm.country',
              })}
            </label>
            <input
              type="country"
              name={'country'}
              value={countryInput}
              onFocus={() => setCountryInputFocused(true)}
              onChange={handleInputChange}
              // onBlur={handleInputBlur}
              className={css.input}
              placeholder={intl.formatMessage({
                id: 'EditListingDetailsForm.countryPlaceholder',
              })}
            />
            {/* {countryError && <div className={css.error}>{countryError}</div>} */}
            {renderablePredictions.length > 0 && showPredictions && (
              <ul className={css.dropdown} ref={countryDropdownRef}>
                {renderablePredictions.map((prediction, index) => (
                  <li
                    key={`${prediction.id}-${index}`}
                    onClick={() => handlePredictionClick(prediction)}
                    onMouseEnter={() => handleMouseEnter(index)}
                    className={css.dropdownItem}
                  >
                    {prediction.place_name}
                  </li>
                ))}
              </ul>
            )}
          </OutsideClickHandler>

          <div>
            <ExpandableDropdown form={formApi} initialValues={values.languages} />
          </div>

          <FieldSelectListingType
            name="listingType"
            listingTypes={selectableListingTypes}
            hasExistingListingType={hasExistingListingType}
            onListingTypeChange={onListingTypeChange}
            formApi={formApi}
            formId={formId}
            intl={intl}
          />

          <div className={css.textareaInputWrapper}>
            <FieldTextInput
              id={`${formId}description`}
              name="description"
              className={css.description}
              type="textarea"
              label={intl.formatMessage({ id: 'EditListingDetailsForm.description' })}
              maxLength={DESCRIPTION_MAX_LENGTH}
              placeholder={intl.formatMessage({
                id: 'EditListingDetailsForm.descriptionPlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'EditListingDetailsForm.descriptionRequired',
                })
              )}
            />

            {/* charactor count */}
            <p className={css.destinationCharCount}>{discriptionLength}/{DESCRIPTION_MAX_LENGTH}</p>
          </div>


          {showListingFields && isCompatibleCurrency && (
            <AddListingFields
              listingType={listingType}
              listingFieldsConfig={listingFieldsConfig}
              selectedCategories={pickSelectedCategories(values)}
              formId={formId}
              intl={intl}
            />
          )}

          {!isCompatibleCurrency && listingType && (
            <p className={css.error}>
              <FormattedMessage
                id="EditListingDetailsForm.incompatibleCurrency"
                values={{ marketplaceName, marketplaceCurrency }}
              />
            </p>
          )}

          <div className={css.submitButtonContainer}>
            <Button
              className={css.submitButton}
              type="submit"
              onClick={() => formApi.change('draftButtonClicked', false)}
              inProgress={submitInProgress && !values?.draftButtonClicked}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>
            {showDraftButton && (
              <Button
                className={css.draftButton}
                type="submit"
                onClick={() => formApi.batch(() => {
                  formApi.change('saveDraftOnly', true);
                  formApi.change('draftButtonClicked', true);
                })}
                inProgress={saveAsDraftInProgress}
                disabled={submitDisabled}
                ready={submitReady || values?.draftButtonClicked}
              >
                {draftButtonText}
              </Button>
            )}
          </div>
        </Form>
      );
    }}
  />
);

EditListingDetailsFormComponent.defaultProps = {
  className: null,
  formId: 'EditListingDetailsForm',
  fetchErrors: null,
  hasExistingListingType: false,
  listingFieldsConfig: [],
};

EditListingDetailsFormComponent.propTypes = {
  className: string,
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onListingTypeChange: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  pickSelectedCategories: func.isRequired,
  selectableListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionProcessAlias: string.isRequired,
      unitType: string.isRequired,
    })
  ).isRequired,
  hasExistingListingType: bool,
  listingFieldsConfig: propTypes.listingFields,
};

export default compose(injectIntl)(EditListingDetailsFormComponent);

